/**
 * Control Configuration Schemas
 * Schema-driven config system for all 14 TrustGrid controls
 *
 * Schema Structure:
 * - sections: Array of form sections
 * - Each section has fields with type, validation, options
 * - Supports: text, number, select, multiselect, checkbox, slider, radio, textarea
 */

// CTRL_01: PII Detection
export const CTRL_01_SCHEMA = {
  controlCode: 'CTRL_01',
  controlName: 'PII Detection & Masking',
  defaultConfig: {
    enabled: false,
    pii_scanning_mode: 'realtime',
    pii_types_enabled: ['email', 'phone', 'ssn', 'credit_card'],
    implementation_path: 'scanner',
    confidence_threshold: 80.0,
    mask_before_processing: true,
    masking_strategy: {
      email: 'partial',
      phone: 'partial',
      ssn: 'redact',
      credit_card: 'redact',
    },
    max_doc_size_kb: 256,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable PII Detection',
          type: 'checkbox',
          tooltip: 'Enable automated PII scanning for all events',
        },
        {
          key: 'pii_scanning_mode',
          label: 'Scanning Mode',
          type: 'radio',
          options: [
            { value: 'realtime', label: 'Real-time', description: 'Scan all events as they arrive' },
            { value: 'async', label: 'Asynchronous', description: 'Queue events for background scanning' },
            { value: 'hybrid', label: 'Hybrid', description: 'Sample-based real-time + full async' },
          ],
        },
        {
          key: 'implementation_path',
          label: 'Implementation Path',
          type: 'select',
          options: [
            { value: 'scanner', label: 'Scanner (Post-collection)' },
            { value: 'instrumented', label: 'Instrumented (Pre-ingestion SDK)' },
            { value: 'hybrid', label: 'Hybrid (Both)' },
          ],
        },
      ],
    },
    {
      key: 'pii_types',
      title: 'PII Types',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'pii_types_enabled',
          label: 'Enabled PII Types',
          type: 'multiselect',
          options: [
            { value: 'email', label: 'Email Address' },
            { value: 'phone', label: 'Phone Number' },
            { value: 'ssn', label: 'Social Security Number' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'ip_address', label: 'IP Address' },
            { value: 'dob', label: 'Date of Birth' },
            { value: 'name', label: 'Person Name' },
            { value: 'address', label: 'Physical Address' },
          ],
        },
        {
          key: 'confidence_threshold',
          label: 'Confidence Threshold',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
          tooltip: 'Minimum confidence percentage to flag PII',
        },
      ],
    },
    {
      key: 'masking',
      title: 'Masking Strategy',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'mask_before_processing',
          label: 'Mask Before Processing',
          type: 'checkbox',
          tooltip: 'Apply masking before storing/analyzing events',
        },
        {
          key: 'masking_strategy.email',
          label: 'Email Masking',
          type: 'select',
          options: [
            { value: 'redact', label: 'Redact ([REDACTED])' },
            { value: 'partial', label: 'Partial (u***@domain.com)' },
            { value: 'hash', label: 'Hash (SHA256)' },
          ],
        },
        {
          key: 'masking_strategy.ssn',
          label: 'SSN Masking',
          type: 'select',
          options: [
            { value: 'redact', label: 'Redact' },
            { value: 'partial', label: 'Partial (***-**-1234)' },
            { value: 'hash', label: 'Hash' },
          ],
        },
      ],
    },
    {
      key: 'performance',
      title: 'Performance Settings',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'max_doc_size_kb',
          label: 'Max Document Size (KB)',
          type: 'number',
          min: 1,
          max: 1024,
          tooltip: 'Skip scanning for documents larger than this',
        },
      ],
    },
  ],
};

// CTRL_02: Data Quality
export const CTRL_02_SCHEMA = {
  controlCode: 'CTRL_02',
  controlName: 'Data Quality Monitoring',
  defaultConfig: {
    enabled: false,
    quality_checks: ['completeness', 'validity', 'consistency'],
    completeness_threshold: 95.0,
    validity_threshold: 90.0,
    check_frequency: 'realtime',
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Data Quality Monitoring',
          type: 'checkbox',
        },
        {
          key: 'quality_checks',
          label: 'Quality Checks',
          type: 'multiselect',
          options: [
            { value: 'completeness', label: 'Completeness' },
            { value: 'validity', label: 'Validity' },
            { value: 'consistency', label: 'Consistency' },
            { value: 'accuracy', label: 'Accuracy' },
            { value: 'timeliness', label: 'Timeliness' },
          ],
        },
      ],
    },
    {
      key: 'thresholds',
      title: 'Quality Thresholds',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'completeness_threshold',
          label: 'Completeness Threshold (%)',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
        {
          key: 'validity_threshold',
          label: 'Validity Threshold (%)',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
      ],
    },
    {
      key: 'frequency',
      title: 'Check Frequency',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'check_frequency',
          label: 'Frequency',
          type: 'radio',
          options: [
            { value: 'realtime', label: 'Real-time' },
            { value: 'hourly', label: 'Hourly' },
            { value: 'daily', label: 'Daily' },
          ],
        },
      ],
    },
  ],
};

// CTRL_03: Infrastructure Filter
export const CTRL_03_SCHEMA = {
  controlCode: 'CTRL_03',
  controlName: 'Infrastructure Event Filtering',
  defaultConfig: {
    enabled: false,
    filter_strategy: 'allowlist',
    infrastructure_keywords: ['kubernetes', 'docker', 'jenkins', 'ci/cd'],
    apply_to_event_types: ['deployment', 'build', 'infrastructure'],
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Infrastructure Filtering',
          type: 'checkbox',
        },
        {
          key: 'filter_strategy',
          label: 'Filter Strategy',
          type: 'radio',
          options: [
            { value: 'allowlist', label: 'Allowlist (Include only infra events)' },
            { value: 'blocklist', label: 'Blocklist (Exclude infra events)' },
          ],
        },
      ],
    },
    {
      key: 'keywords',
      title: 'Infrastructure Keywords',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'infrastructure_keywords',
          label: 'Keywords',
          type: 'tags',
          placeholder: 'Add infrastructure keyword...',
          tooltip: 'Keywords to identify infrastructure events',
        },
        {
          key: 'apply_to_event_types',
          label: 'Apply to Event Types',
          type: 'multiselect',
          options: [
            { value: 'deployment', label: 'Deployment' },
            { value: 'build', label: 'Build' },
            { value: 'infrastructure', label: 'Infrastructure' },
            { value: 'configuration', label: 'Configuration' },
          ],
        },
      ],
    },
  ],
};

// CTRL_04: Explainability & Transparency
export const CTRL_04_SCHEMA = {
  controlCode: 'CTRL_04',
  controlName: 'Explainability & Transparency',
  defaultConfig: {
    enabled: false,
    required_explainability_methods: ['SHAP', 'LIME'],
    require_confidence_scores: true,
    minimum_confidence_threshold: 70,
    customer_facing_only: false,
    explanation_quality_threshold: 60,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Explainability Control',
          type: 'checkbox',
          tooltip: 'Require explainability metadata for inference events',
        },
      ],
    },
    {
      key: 'methods',
      title: 'Explainability Methods',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'required_explainability_methods',
          label: 'Accepted Methods',
          type: 'multiselect',
          options: [
            { value: 'SHAP', label: 'SHAP (SHapley Additive exPlanations)' },
            { value: 'LIME', label: 'LIME (Local Interpretable Model-agnostic)' },
            { value: 'attention', label: 'Attention Mechanisms (Neural Networks)' },
            { value: 'gradient', label: 'Gradient-based (Integrated Gradients)' },
            { value: 'counterfactual', label: 'Counterfactual (What-if scenarios)' },
          ],
        },
        {
          key: 'customer_facing_only',
          label: 'Customer-facing Models Only',
          type: 'checkbox',
          tooltip: 'Apply requirement only to customer-facing models',
        },
      ],
    },
    {
      key: 'confidence',
      title: 'Confidence Requirements',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'require_confidence_scores',
          label: 'Require Confidence Scores',
          type: 'checkbox',
        },
        {
          key: 'minimum_confidence_threshold',
          label: 'Minimum Confidence Threshold (%)',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
        {
          key: 'explanation_quality_threshold',
          label: 'Explanation Quality Threshold (%)',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
      ],
    },
  ],
};

// CTRL_05: Training Data Audit Trail
export const CTRL_05_SCHEMA = {
  controlCode: 'CTRL_05',
  controlName: 'Training Data Audit Trail',
  defaultConfig: {
    enabled: false,
    require_dataset_manifest: true,
    require_consent_tracking: true,
    consent_types: ['explicit', 'legitimate_interest'],
    minimum_data_quality_score: 75,
    require_ropa_documentation: false,
    require_data_source_approval: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Audit Trail Control',
          type: 'checkbox',
        },
        {
          key: 'require_dataset_manifest',
          label: 'Require Dataset Manifest',
          type: 'checkbox',
          tooltip: 'Training events must link to dataset_id',
        },
        {
          key: 'require_data_source_approval',
          label: 'Require Data Source Approval',
          type: 'checkbox',
        },
      ],
    },
    {
      key: 'consent',
      title: 'Consent Tracking',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'require_consent_tracking',
          label: 'Require Consent Tracking',
          type: 'checkbox',
        },
        {
          key: 'consent_types',
          label: 'Acceptable Consent Types',
          type: 'multiselect',
          options: [
            { value: 'explicit', label: 'Explicit Consent (Active opt-in)' },
            { value: 'implicit', label: 'Implicit Consent (Assumed)' },
            { value: 'legitimate_interest', label: 'Legitimate Interest (GDPR Art 6)' },
            { value: 'not_required', label: 'Not Required (Non-personal data)' },
          ],
        },
      ],
    },
    {
      key: 'quality',
      title: 'Data Quality',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'minimum_data_quality_score',
          label: 'Minimum Quality Score',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
        {
          key: 'require_ropa_documentation',
          label: 'Require ROPA Documentation',
          type: 'checkbox',
          tooltip: 'Record of Processing Activities (GDPR Art 30)',
        },
      ],
    },
  ],
};

// CTRL_06: Model Performance Monitoring
export const CTRL_06_SCHEMA = {
  controlCode: 'CTRL_06',
  controlName: 'Model Performance Monitoring',
  defaultConfig: {
    enabled: false,
    required_metrics: ['accuracy', 'latency_ms'],
    degradation_threshold_pct: 5,
    monitoring_interval: 'daily',
    enable_drift_detection: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Performance Monitoring',
          type: 'checkbox',
        },
        {
          key: 'required_metrics',
          label: 'Required Metrics',
          type: 'multiselect',
          options: [
            { value: 'accuracy', label: 'Accuracy' },
            { value: 'precision', label: 'Precision' },
            { value: 'recall', label: 'Recall' },
            { value: 'f1_score', label: 'F1 Score' },
            { value: 'latency_ms', label: 'Latency (ms)' },
            { value: 'throughput', label: 'Throughput (req/sec)' },
            { value: 'error_rate', label: 'Error Rate' },
          ],
        },
      ],
    },
    {
      key: 'thresholds',
      title: 'Alert Thresholds',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'degradation_threshold_pct',
          label: 'Degradation Threshold (%)',
          type: 'number',
          min: 0,
          max: 100,
          tooltip: 'Alert if metrics degrade by this percentage',
        },
        {
          key: 'monitoring_interval',
          label: 'Monitoring Interval',
          type: 'select',
          options: [
            { value: 'realtime', label: 'Real-time' },
            { value: 'hourly', label: 'Hourly' },
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
          ],
        },
        {
          key: 'enable_drift_detection',
          label: 'Enable Drift Detection',
          type: 'checkbox',
        },
      ],
    },
  ],
};

// CTRL_07: Bias & Fairness Testing
export const CTRL_07_SCHEMA = {
  controlCode: 'CTRL_07',
  controlName: 'Bias & Fairness Testing',
  defaultConfig: {
    enabled: false,
    fairness_metrics: ['demographic_parity', 'disparate_impact'],
    protected_attributes: [],
    bias_threshold: 0.2,
    test_frequency: 'quarterly',
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Bias & Fairness Testing',
          type: 'checkbox',
        },
        {
          key: 'fairness_metrics',
          label: 'Fairness Metrics',
          type: 'multiselect',
          options: [
            { value: 'demographic_parity', label: 'Demographic Parity' },
            { value: 'equalized_odds', label: 'Equalized Odds' },
            { value: 'disparate_impact', label: 'Disparate Impact (80% Rule)' },
            { value: 'equal_opportunity', label: 'Equal Opportunity' },
          ],
        },
      ],
    },
    {
      key: 'attributes',
      title: 'Protected Attributes',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'protected_attributes',
          label: 'Test for Bias Across',
          type: 'multiselect',
          options: [
            { value: 'gender', label: 'Gender' },
            { value: 'race', label: 'Race/Ethnicity' },
            { value: 'age', label: 'Age' },
            { value: 'ethnicity', label: 'Ethnicity' },
            { value: 'disability', label: 'Disability Status' },
            { value: 'religion', label: 'Religion' },
          ],
        },
      ],
    },
    {
      key: 'thresholds',
      title: 'Test Configuration',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'bias_threshold',
          label: 'Bias Threshold',
          type: 'slider',
          min: 0,
          max: 1,
          step: 0.05,
          tooltip: 'Maximum acceptable bias score (0-1)',
        },
        {
          key: 'test_frequency',
          label: 'Test Frequency',
          type: 'select',
          options: [
            { value: 'pre_deployment', label: 'Pre-deployment Only' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'annually', label: 'Annually' },
          ],
        },
      ],
    },
  ],
};

// CTRL_08: Human Oversight & Review Gates
export const CTRL_08_SCHEMA = {
  controlCode: 'CTRL_08',
  controlName: 'Human Oversight & Review Gates',
  defaultConfig: {
    enabled: false,
    require_approval_for: ['high_risk_models', 'model_deployment'],
    approval_workflow: 'single_approver',
    log_reviewer_identity: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Human Oversight Control',
          type: 'checkbox',
        },
        {
          key: 'require_approval_for',
          label: 'Require Approval For',
          type: 'multiselect',
          options: [
            { value: 'high_risk_models', label: 'High-risk Model Predictions' },
            { value: 'model_deployment', label: 'Model Deployments' },
            { value: 'data_changes', label: 'Training Data Changes' },
            { value: 'config_changes', label: 'Configuration Changes' },
          ],
        },
      ],
    },
    {
      key: 'workflow',
      title: 'Approval Workflow',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'approval_workflow',
          label: 'Workflow Type',
          type: 'select',
          options: [
            { value: 'single_approver', label: 'Single Approver' },
            { value: 'dual_approver', label: 'Dual Approval Required' },
            { value: 'committee', label: 'Committee Review' },
          ],
        },
        {
          key: 'log_reviewer_identity',
          label: 'Log Reviewer Identity',
          type: 'checkbox',
          tooltip: 'Record reviewer name and timestamp',
        },
      ],
    },
  ],
};

// CTRL_09: Data Residency & Sovereignty
export const CTRL_09_SCHEMA = {
  controlCode: 'CTRL_09',
  controlName: 'Data Residency & Sovereignty',
  defaultConfig: {
    enabled: false,
    allowed_regions: [],
    block_cross_border_transfer: false,
    require_sccs: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Data Residency Control',
          type: 'checkbox',
        },
        {
          key: 'allowed_regions',
          label: 'Allowed Regions',
          type: 'multiselect',
          options: [
            { value: 'us-east-1', label: 'US East (N. Virginia)' },
            { value: 'us-west-2', label: 'US West (Oregon)' },
            { value: 'eu-west-1', label: 'EU West (Ireland)' },
            { value: 'eu-central-1', label: 'EU Central (Frankfurt)' },
            { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
            { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
          ],
        },
      ],
    },
    {
      key: 'transfers',
      title: 'Cross-border Transfers',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'block_cross_border_transfer',
          label: 'Block Cross-border Transfers',
          type: 'checkbox',
          tooltip: 'Prevent data processing outside allowed regions',
        },
        {
          key: 'require_sccs',
          label: 'Require Standard Contractual Clauses',
          type: 'checkbox',
          tooltip: 'SCCs required for international transfers (GDPR)',
        },
      ],
    },
  ],
};

// CTRL_10: Documentation & Model Cards
export const CTRL_10_SCHEMA = {
  controlCode: 'CTRL_10',
  controlName: 'Documentation & Model Cards',
  defaultConfig: {
    enabled: false,
    require_model_card: true,
    require_impact_assessment: true,
    require_technical_docs: true,
    model_card_template: 'standard',
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Documentation Control',
          type: 'checkbox',
        },
        {
          key: 'require_model_card',
          label: 'Require Model Cards',
          type: 'checkbox',
          tooltip: 'Model cards documenting purpose, performance, limitations',
        },
        {
          key: 'require_impact_assessment',
          label: 'Require Impact Assessment',
          type: 'checkbox',
        },
        {
          key: 'require_technical_docs',
          label: 'Require Technical Documentation',
          type: 'checkbox',
        },
      ],
    },
    {
      key: 'templates',
      title: 'Documentation Templates',
      collapsible: true,
      defaultExpanded: false,
      fields: [
        {
          key: 'model_card_template',
          label: 'Model Card Template',
          type: 'select',
          options: [
            { value: 'standard', label: 'Standard (Google)' },
            { value: 'eu_ai_act', label: 'EU AI Act Compliant' },
            { value: 'custom', label: 'Custom Template' },
          ],
        },
      ],
    },
  ],
};

// CTRL_11: Incident Response & Rollback
export const CTRL_11_SCHEMA = {
  controlCode: 'CTRL_11',
  controlName: 'Incident Response & Rollback Capability',
  defaultConfig: {
    enabled: false,
    require_rollback_plan: true,
    deployment_strategy: 'canary',
    rollback_threshold_minutes: 30,
    auto_rollback_on_failure: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Incident Response Control',
          type: 'checkbox',
        },
        {
          key: 'require_rollback_plan',
          label: 'Require Rollback Plan',
          type: 'checkbox',
          tooltip: 'Deployments must include rollback procedures',
        },
      ],
    },
    {
      key: 'deployment',
      title: 'Deployment Strategy',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'deployment_strategy',
          label: 'Strategy',
          type: 'select',
          options: [
            { value: 'blue_green', label: 'Blue-Green' },
            { value: 'canary', label: 'Canary' },
            { value: 'rolling', label: 'Rolling Update' },
          ],
        },
        {
          key: 'auto_rollback_on_failure',
          label: 'Auto-rollback on Failure',
          type: 'checkbox',
        },
        {
          key: 'rollback_threshold_minutes',
          label: 'Rollback Threshold (minutes)',
          type: 'number',
          min: 1,
          max: 1440,
          tooltip: 'Time window for automatic rollback decision',
        },
      ],
    },
  ],
};

// CTRL_12: Purpose Limitation
export const CTRL_12_SCHEMA = {
  controlCode: 'CTRL_12',
  controlName: 'Purpose Limitation',
  defaultConfig: {
    enabled: false,
    allowed_purposes: [],
    require_consent: true,
    require_documentation: true,
    purpose_change_notification: true,
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Purpose Limitation Control',
          type: 'checkbox',
        },
        {
          key: 'allowed_purposes',
          label: 'Allowed Purposes',
          type: 'tags',
          placeholder: 'Add purpose (e.g., fraud detection)...',
          tooltip: 'Define legitimate purposes for model usage',
        },
      ],
    },
    {
      key: 'compliance',
      title: 'Compliance Requirements',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'require_consent',
          label: 'Require User Consent',
          type: 'checkbox',
          tooltip: 'Consent must match stated purpose',
        },
        {
          key: 'require_documentation',
          label: 'Require Purpose Documentation',
          type: 'checkbox',
        },
        {
          key: 'purpose_change_notification',
          label: 'Notify on Purpose Change',
          type: 'checkbox',
          tooltip: 'Alert users when purpose changes',
        },
      ],
    },
  ],
};

// CTRL_13: Data Provenance (Detailed)
export const CTRL_13_SCHEMA = {
  controlCode: 'CTRL_13',
  controlName: 'Data Provenance (Detailed)',
  defaultConfig: {
    enabled: false,
    require_lineage_tracking: true,
    minimum_quality_score: 80,
    require_version_control: true,
    allowed_data_sources: ['s3', 'gcs', 'redshift', 'bigquery'],
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Data Provenance Control',
          type: 'checkbox',
        },
        {
          key: 'require_lineage_tracking',
          label: 'Require Lineage Tracking',
          type: 'checkbox',
          tooltip: 'Track data transformations and lineage DAG',
        },
        {
          key: 'require_version_control',
          label: 'Require Version Control',
          type: 'checkbox',
        },
      ],
    },
    {
      key: 'sources',
      title: 'Data Sources',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'allowed_data_sources',
          label: 'Allowed Data Sources',
          type: 'multiselect',
          options: [
            { value: 's3', label: 'AWS S3' },
            { value: 'gcs', label: 'Google Cloud Storage' },
            { value: 'redshift', label: 'Amazon Redshift' },
            { value: 'bigquery', label: 'Google BigQuery' },
            { value: 'snowflake', label: 'Snowflake' },
            { value: 'databricks', label: 'Databricks' },
          ],
        },
        {
          key: 'minimum_quality_score',
          label: 'Minimum Quality Score',
          type: 'slider',
          min: 0,
          max: 100,
          step: 5,
        },
      ],
    },
  ],
};

// CTRL_14: Data Minimization
export const CTRL_14_SCHEMA = {
  controlCode: 'CTRL_14',
  controlName: 'Data Minimization',
  defaultConfig: {
    enabled: false,
    enforce_minimization: true,
    max_retention_days: 365,
    auto_remove_flagged_features: false,
    minimization_strategy: 'hybrid',
  },
  sections: [
    {
      key: 'basic',
      title: 'Basic Configuration',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'enabled',
          label: 'Enable Data Minimization Control',
          type: 'checkbox',
        },
        {
          key: 'enforce_minimization',
          label: 'Enforce Minimization',
          type: 'checkbox',
          tooltip: 'Require removal of unnecessary features',
        },
      ],
    },
    {
      key: 'retention',
      title: 'Retention & Removal',
      collapsible: true,
      defaultExpanded: true,
      fields: [
        {
          key: 'max_retention_days',
          label: 'Max Retention (days)',
          type: 'number',
          min: 1,
          max: 3650,
          tooltip: 'Maximum data retention period',
        },
        {
          key: 'auto_remove_flagged_features',
          label: 'Auto-remove Flagged Features',
          type: 'checkbox',
          tooltip: 'Automatically remove low-importance features',
        },
        {
          key: 'minimization_strategy',
          label: 'Minimization Strategy',
          type: 'select',
          options: [
            { value: 'manual', label: 'Manual Review' },
            { value: 'automated', label: 'Automated' },
            { value: 'hybrid', label: 'Hybrid (Review + Auto)' },
          ],
        },
      ],
    },
  ],
};

// Schema registry - maps control codes to schemas
export const CONTROL_SCHEMAS = {
  CTRL_01: CTRL_01_SCHEMA,
  CTRL_02: CTRL_02_SCHEMA,
  CTRL_03: CTRL_03_SCHEMA,
  CTRL_04: CTRL_04_SCHEMA,
  CTRL_05: CTRL_05_SCHEMA,
  CTRL_06: CTRL_06_SCHEMA,
  CTRL_07: CTRL_07_SCHEMA,
  CTRL_08: CTRL_08_SCHEMA,
  CTRL_09: CTRL_09_SCHEMA,
  CTRL_10: CTRL_10_SCHEMA,
  CTRL_11: CTRL_11_SCHEMA,
  CTRL_12: CTRL_12_SCHEMA,
  CTRL_13: CTRL_13_SCHEMA,
  CTRL_14: CTRL_14_SCHEMA,
};

/**
 * Get schema for a control code
 * @param {string} controlCode - Control code (e.g., 'CTRL_01')
 * @returns {Object|null} Schema object or null
 */
export function getControlSchema(controlCode) {
  return CONTROL_SCHEMAS[controlCode] || null;
}

/**
 * Check if control has a custom schema
 * @param {string} controlCode - Control code
 * @returns {boolean} True if schema exists
 */
export function hasControlSchema(controlCode) {
  return controlCode in CONTROL_SCHEMAS;
}
